/**
 * Sharing: channels, intent URLs, card formats, invite landing DTO, attribution cookie.
 * WeChat has no web intent without a Chinese-registered JS-SDK → square card with an invite QR (UI).
 */
import { z } from 'zod';
import { zLocale, type GameId, type Locale } from './common';
import type { PublicAwardBrief } from './achievements';

export const SHARE_CHANNELS = ['sys', 'wx', 'qq', 'wb', 'tg', 'wa', 'ln', 'x', 'fb', 'vk', 'dl', 'cp', 'qr'] as const;
export type ShareChannel = (typeof SHARE_CHANNELS)[number];
export const zShareChannel = z.enum(SHARE_CHANNELS);

/** a win may be shared from ×50 (hundredths) and only if the win is at least the stake */
export const SHARE_WIN_MIN_X100 = 5000;
/** big-win moments a player may create per UTC day */
export const MOMENTS_PER_DAY = 20;

export const CARD_FORMATS = ['og', 'sq'] as const;
export type CardFormat = (typeof CARD_FORMATS)[number];
export const CARD_SIZE: Record<CardFormat, { w: number; h: number }> = { og: { w: 1200, h: 630 }, sq: { w: 1080, h: 1080 } };

/** the four covers on the invite landing and invite card */
export const INVITE_FEATURED_GAMES = ['koi-pond', 'carp', 'mahjong', 'crash'] as const satisfies readonly GameId[];

/** `<channel>.<awardId|->` — set by the public pages, read by the sign-up hook (7 days) */
export const SOURCE_COOKIE = 'tg_src';

const PRIMARY: Record<Locale, ShareChannel[]> = {
  zh: ['wx', 'qq', 'wb', 'sys'],
  ru: ['tg', 'vk', 'wa', 'sys'],
  de: ['wa', 'tg', 'fb', 'x', 'sys'],
  en: ['wa', 'x', 'fb', 'tg', 'ln', 'sys'],
};

/** primary channels of the locale first, then the rest in SHARE_CHANNELS order */
export function channelOrder(locale: Locale): ShareChannel[] {
  const p = PRIMARY[locale];
  return [...p, ...SHARE_CHANNELS.filter((c) => !p.includes(c))];
}

export interface ShareSubject {
  url: string;
  text: string;
  /** absolute URL of the og card */
  image: string;
}

const e = encodeURIComponent;

/** Web intent of a channel; null for channels handled by the UI (system share, WeChat, download, copy, QR). */
export function channelUrl(ch: ShareChannel, s: ShareSubject): string | null {
  switch (ch) {
    case 'tg':
      return `https://t.me/share/url?url=${e(s.url)}&text=${e(s.text)}`;
    case 'wa':
      return `https://wa.me/?text=${e(`${s.text} ${s.url}`)}`;
    case 'ln':
      return `https://social-plugins.line.me/lineit/share?url=${e(s.url)}`;
    case 'x':
      return `https://x.com/intent/post?text=${e(s.text)}&url=${e(s.url)}`;
    case 'fb':
      return `https://www.facebook.com/sharer/sharer.php?u=${e(s.url)}`;
    case 'vk':
      return `https://vk.com/share.php?url=${e(s.url)}&title=${e(s.text)}&image=${e(s.image)}`;
    case 'wb':
      return `https://service.weibo.com/share/share.php?url=${e(s.url)}&title=${e(s.text)}&pic=${e(s.image)}`;
    case 'qq':
      return `https://connect.qq.com/widget/shareqq/index.html?url=${e(s.url)}&title=${e(s.text)}&pics=${e(s.image)}`;
    default:
      return null;
  }
}

export function sourceCookie(channel: ShareChannel, awardId: string | null): string {
  return `${channel}.${awardId ?? '-'}`;
}

export function parseSourceCookie(v: string | undefined | null): { channel: ShareChannel; awardId: string | null } | null {
  const m = /^([a-z]{1,3})\.([A-HJ-NP-Z2-9]{12}|-)$/.exec(v ?? '');
  if (!m) return null;
  const ch = zShareChannel.safeParse(m[1]);
  return ch.success ? { channel: ch.data, awardId: m[2] === '-' ? null : m[2]! } : null;
}

/** POST /api/share/awards/:id, POST /api/share/moments, GET /api/share/invite */
export interface ShareTarget {
  /** canonical public URL without the channel (the sheet appends `?c=<channel>`) */
  url: string;
  /** neutral share text in the sharer's locale (no token amounts) */
  text: string;
  images: Record<CardFormat, string>;
  channels: ShareChannel[];
}

/** GET /api/public/invites/:code */
export interface PublicInvite {
  code: string;
  inviter: { nickname: string; avatar: string } | null;
  games: GameId[];
  awards: PublicAwardBrief[];
  signupBonus: number;
  cards: Record<CardFormat, string>;
}

/** the sharer's UI language → card, OG and share text language */
export const zShareRequest = z.object({ locale: zLocale.optional() }).strict();
export const zMomentCreate = z.object({ betId: z.string().uuid(), locale: zLocale.optional() }).strict();
export const zRevoke = z.object({ comment: z.string().trim().min(3).max(500) }).strict();
